import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient, { API_BASE_URL } from '../utils/apiClient';
import { LazyLoadImage } from 'react-lazy-load-image-component';
// Removed blur effect import for better PNG transparency
import './Feed.css';
import Navbar from '../components/Navbar';
import LoadingLogo from '../components/LoadingLogo';
import FunFact from '../components/FunFact';
import { TabContainer, EmulatorContainer } from '../components/emulators';
import { refreshRecommendations } from '../utils/recommendationEngine';
import {
  useDragControls,
  useMotionValue,
  useAnimate,
  motion,
} from 'framer-motion';
import useMeasure from 'react-use-measure';


const Feed = () => {
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [allGamesData, setAllGamesData] = useState([]); // store full games list for future recommendations
  const [platforms, setPlatforms] = useState({});
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState(null);
  const [currentGameIndex, setCurrentGameIndex] = useState(0);
  const [feedGames, setFeedGames] = useState([]);
  const [feedStartIndex, setFeedStartIndex] = useState(0);
  const [currentGame, setCurrentGame] = useState(null);
  const [showManualSidebar, setShowManualSidebar] = useState(false);
  const [showControlsSidebar, setShowControlsSidebar] = useState(false);
  const [manuals, setManuals] = useState([]);
  const [activeManualIndex, setActiveManualIndex] = useState(0);
  const feedContainerRef = useRef(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);
  const [gameTags, setGameTags] = useState([]);
  const [platform, setPlatform] = useState(null);
  const [controlsData, setControlsData] = useState(null);
  // Adjust number of visible games for small screens
  const MAX_VISIBLE_GAMES = (typeof window !== 'undefined' && window.innerWidth <= 1024) ? 3 : 5;
  // Maximum number of games we keep in memory to avoid an ever-growing queue
  const MAX_QUEUE_LENGTH = 45;

  // Add state for ROM version
  const [romVersion, setRomVersion] = useState('默认');
  const [romVersions, setRomVersions] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedRomPath, setSelectedRomPath] = useState(null);
  const [selectedRomData, setSelectedRomData] = useState(null);

  // First-time user experience states
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [showInstructionalIcon, setShowInstructionalIcon] = useState(false);
  const [showBounceAnimation, setShowBounceAnimation] = useState(false);

  // Memoize the game object to prevent unnecessary re-renders
  const memoizedGame = useMemo(() => {
    if (!currentGame) {
      console.log(`[DEBUG] memoizedGame is null - currentGame: ${!!currentGame}`);
      return null;
    }
    
    // Use selected ROM data if available, otherwise fall back to game's default ROM
    let romPath = selectedRomPath;
    let romData = selectedRomData;
    
    // Fallback to game's default ROM if ROM selection state is not ready
    if (!romPath && currentGame.rom) {
      romPath = currentGame.rom;
      romData = { path: currentGame.rom, type: '默认' };
      console.log(`[DEBUG] memoizedGame using fallback ROM for game ${currentGame.id}: ${romPath}`);
    }
    
    if (!romPath) {
      console.log(`[DEBUG] memoizedGame waiting for ROM data - selectedRomPath: ${!!selectedRomPath}, game.rom: ${!!currentGame.rom}`);
      return null;
    }
    
    console.log(`[DEBUG] memoizedGame updated for game ${currentGame.id} with ROM: ${romPath}`);
    return {
      ...currentGame,
      rom: romPath,
      romData: romData,
      activeRom: romPath // Add activeRom for EmulatorJS compatibility
    };
  }, [currentGame, selectedRomPath, selectedRomData]);
  const [isDescriptionScrollable, setIsDescriptionScrollable] = useState(false);
  
  // Debug info from recommendation engine
  const [recommendationDebug, setRecommendationDebug] = useState(null);
  // Store debug info for each batch of games
  const [batchDebugInfo, setBatchDebugInfo] = useState([]);
  
  // === Onboarding queue progress management ===
  const getOnboardingProgress = () => {
    try {
      const progress = localStorage.getItem('onboardingProgress');
      return progress ? JSON.parse(progress) : null;
    } catch (err) {
      console.error('Error reading onboarding progress:', err);
      return null;
    }
  };

  const saveOnboardingProgress = (queueGames, currentIndex) => {
    try {
      const progress = {
        queueGameIds: queueGames.map(g => g.id),
        currentIndex: currentIndex,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('onboardingProgress', JSON.stringify(progress));
      console.log(`[Onboarding] Progress saved: ${currentIndex + 1}/${queueGames.length} games viewed`);
    } catch (err) {
      console.error('Error saving onboarding progress:', err);
    }
  };

  const clearOnboardingProgress = () => {
    try {
      localStorage.removeItem('onboardingProgress');
      console.log('[Onboarding] Queue completed, progress cleared');
    } catch (err) {
      console.error('Error clearing onboarding progress:', err);
    }
  };

  // === New: track viewed games ===
  const recordGameView = (gameId) => {
    if (!gameId) return;
    try {
      const rawViewed = JSON.parse(localStorage.getItem('viewedGames') || '[]');
      rawViewed.push(gameId); // Store only the ID, duplicates allowed to reflect multiple views
      
      // Keep only the most recent 300 viewed games
      if (rawViewed.length > 300) {
        rawViewed.splice(0, rawViewed.length - 300);
      }
      
      localStorage.setItem('viewedGames', JSON.stringify(rawViewed));

      // Check if user is in onboarding queue and update progress
      const onboardingProgress = getOnboardingProgress();
      if (onboardingProgress && onboardingProgress.queueGameIds) {
        const gameIndex = onboardingProgress.queueGameIds.indexOf(gameId);
        if (gameIndex !== -1) {
          // User viewed a game from the onboarding queue
          saveOnboardingProgress(
            onboardingProgress.queueGameIds.map(id => ({ id })),
            gameIndex
          );
          
          // Check if user has finished the entire queue
          if (gameIndex === onboardingProgress.queueGameIds.length - 1) {
            clearOnboardingProgress();
          }
        }
      }
    } catch (err) {
      console.error('Error recording game view:', err);
    }
  };
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  // Favorites management functions
  const getFavoritesFromStorage = () => {
    try {
      const stored = localStorage.getItem('rgm_favorite_games');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading favorites from localStorage:', error);
      return [];
    }
  };

  const saveFavoritesToStorage = (favoriteIds) => {
    try {
      localStorage.setItem('rgm_favorite_games', JSON.stringify(favoriteIds));
    } catch (error) {
      console.error('Error saving favorites to localStorage:', error);
    }
  };

  const [favoriteGameIds, setFavoriteGameIds] = useState(getFavoritesFromStorage);

  const toggleFavorite = useCallback((gameId) => {
    setFavoriteGameIds(prevIds => {
      const newIds = prevIds.includes(gameId) 
        ? prevIds.filter(id => id !== gameId)
        : [...prevIds, gameId];
      
      saveFavoritesToStorage(newIds);
      return newIds;
    });
  }, []);

  const isFavorite = useCallback((gameId) => {
    return favoriteGameIds.includes(gameId);
  }, [favoriteGameIds]);
  
  // Enhanced cache for metadata (platform data, controls, and game details)
  const metadataCache = useRef(new Map());
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  
  // Background image preloading for mobile
  const [preloadedBackgrounds, setPreloadedBackgrounds] = useState(new Set());
  
  // Mobile swipe handling
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeAnimation, setSwipeAnimation] = useState('');
  const touchStartXRef = useRef(0);
  const touchEndXRef = useRef(0);
  const touchStartYRef = useRef(0); // Added for vertical swipe
  const touchEndYRef = useRef(0); // Added for vertical swipe
  const gameDetailsRef = useRef(null);
  const descriptionRef = useRef(null); // Ref for description scroll reset
  // Mobile play/version combo UI
  const [isVersionMenuOpen, setIsVersionMenuOpen] = useState(false);
  const playComboRef = useRef(null);
  const swipeThreshold = 75; // Minimum distance required for a swipe to register
  
  // Drag-to-close functionality for mobile modals
  const [manualScope, animateManual] = useAnimate();
  const [controlsScope, animateControls] = useAnimate();
  const [manualDrawerRef, { height: manualHeight }] = useMeasure();
  const [controlsDrawerRef, { height: controlsHeight }] = useMeasure();
  const manualY = useMotionValue(0);
  const controlsY = useMotionValue(0);
  const manualDragControls = useDragControls();
  const controlsDragControls = useDragControls();
  const isMobileView = useRef(typeof window !== 'undefined' && window.innerWidth <= 1024);
  
  // Time tracking ref
  const timeSpentRef = useRef(0);
  const timerIntervalRef = useRef(null);

  // Background image preloading function for mobile
  const preloadBackgroundImage = (imageUrl) => {
    if (!imageUrl || preloadedBackgrounds.has(imageUrl)) return;
    
    const img = new Image();
    img.onload = () => {
      setPreloadedBackgrounds(prev => new Set([...prev, imageUrl]));
    };
    img.onerror = () => {
      console.warn('Failed to preload background image:', imageUrl);
    };
    img.src = imageUrl;
  };

  // Handle game selection from carousel
  const handleGameSelect = (index) => {
    if (index >= 0 && index < games.length && !isTransitioning) {
      setIsTransitioning(true);
      
      // Check if a game is currently playing and exit it first
      if (isPlaying) {
        handleExitGame();
      }
      
      let adjustedIndex = index;
      let updatedGames = games;
      
      // If we are near the end, load more games
      if (games.length - index <= 3 && allGamesData.length > 0 && Object.keys(platforms).length > 0) {
        const { games: newRecommendations, debug } = refreshRecommendations(allGamesData, platforms);
        
        const newBatchSize = Math.min(15, newRecommendations.length);
        const newBatch = newRecommendations.slice(0, newBatchSize);
        
        updatedGames = [...games, ...newBatch];
        setGames(updatedGames);
        
        setRecommendationDebug(debug);
        
        const batchInfo = {
          batchNumber: batchDebugInfo.length + 1,
          timestamp: new Date().toISOString(),
          debug: JSON.parse(JSON.stringify(debug)),
          gamesCount: newBatch.length,
          games: newBatch.map(game => ({
            id: game.id,
            title: game.titlechn || game.titleeng,
            scores: debug.detailedScores.find(score => score.game.id === game.id) || {}
          }))
        };
        
        setBatchDebugInfo(prevBatches => [...prevBatches, batchInfo]);
        
        // Preload metadata for the first few games in the new batch
        const gamesToPreload = newBatch.slice(0, Math.min(5, newBatch.length));
        gamesToPreload.forEach((game, index) => {
          setTimeout(() => {
            preloadNextGameMetadata(game.id);
          }, (index + 1) * 200); // Stagger with longer delays to not overwhelm
        });
        
        console.log(`Added ${newBatch.length} new games, total now: ${updatedGames.length}`);
      }
      
      // Update the current game index first
      setCurrentGameIndex(adjustedIndex);
      
      // Update the visible games in the carousel
      updateVisibleGames(adjustedIndex, updatedGames);
      
      // Set current game and check for cached metadata
      const selectedGame = updatedGames[adjustedIndex];
      if (selectedGame && selectedGame.id) {
        console.log(`[DEBUG] Selecting game ${selectedGame.id}: ${selectedGame.titlechn || selectedGame.titleeng}`);
        
        // Preload background image for mobile to prevent loading issues
        if (isMobileView.current && selectedGame.cover) {
          preloadBackgroundImage(`${API_BASE_URL}${selectedGame.cover}`);
        }
        
        // Check if we have cached metadata
        const cachedMetadata = metadataCache.current.get(selectedGame.id);
        
        if (cachedMetadata) {
          console.log(`[DEBUG] Using cached metadata for game ${selectedGame.id}`);
          setCurrentGame(selectedGame);
          setPlatform(cachedMetadata.platform);
          setGameTags(cachedMetadata.tags);
          setControlsData(cachedMetadata.controls);
          setManuals(cachedMetadata.manuals);
          setActiveManualIndex(0);
          
          // Set ROM versions
          const versions = getRomVersions(cachedMetadata.gameData);
          setRomVersions(versions);
          if (versions.length > 0) {
            setRomVersion(versions[0].type);
          } else {
            setRomVersion('默认');
          }
        } else {
          console.log(`[DEBUG] Setting current game ${selectedGame.id}, metadata will load separately`);
          setCurrentGame(selectedGame);
          
          // Reset ROM state when switching to a new game to prevent mismatch
          setSelectedRomPath(null);
          setSelectedRomData(null);
          setRomVersion('默认');
        }
        recordGameView(selectedGame.id);
      }
      
      // Maintain preload buffer for upcoming games
      setTimeout(() => {
        maintainPreloadBuffer(adjustedIndex);
      }, 50);
      
      setTimeout(() => setIsTransitioning(false), 300);
    }
  };

  // Prevent Safari mobile pull-to-refresh and navbar hiding
  useEffect(() => {
    let touchStartY = 0;
    let lastTouchY = 0; // eslint-disable-line no-unused-vars
    
    const preventPullToRefresh = (e) => {
      const touch = e.touches[0];
      const currentY = touch.clientY;
      
      // Get the target element
      const target = e.target;
      
      // Allow scrolling within specific scrollable elements (description, sidebars)
      const isScrollableElement = target.closest('.feed-game-description, .feed-manual-sidebar, .feed-controls-sidebar, .manual-container, .keyboard-mappings');
      
      if (isScrollableElement) {
        // Let scrollable elements handle their own scrolling
        return;
      }
      
      // Prevent pull-to-refresh when at the top and pulling down
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      if (scrollTop === 0 && currentY > touchStartY) {
        // Pulling down from the top - prevent default to stop Safari navbar from hiding
        e.preventDefault();
      }
      
      lastTouchY = currentY;
    };
    
    const handleTouchStart = (e) => {
      if (e.touches && e.touches.length > 0) {
        touchStartY = e.touches[0].clientY;
        lastTouchY = touchStartY;
      }
    };
    
    const resetTouch = () => {
      lastTouchY = 0;
      touchStartY = 0;
    };
    
    // Add passive: false to allow preventDefault
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', preventPullToRefresh, { passive: false });
    document.addEventListener('touchend', resetTouch, { passive: true });
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', preventPullToRefresh);
      document.removeEventListener('touchend', resetTouch);
    };
  }, []);

  // Fetch games and platforms on component mount
  useEffect(() => {
    // Disable scroll to prevent overscroll behavior on mobile
    if (isMobileView.current) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100vh';
    }
    
    const fetchData = async () => {
      try {
        setLoading(true);
        const [gamesRes, platformsRes] = await Promise.all([
          apiClient.get('/games'),
          apiClient.get('/platforms')
        ]);
        
        const allGamesDataResp = gamesRes.data;
        const platformsData = platformsRes.data;

        const platformMap = {};
        platformsData.forEach(p => {
          platformMap[p.id] = p;
        });
        setPlatforms(platformMap);

        // Prepare games with platform info and empty tags array
        let gamesWithTags = [...allGamesDataResp];
        
        // Filter out non-mobile optimized games on mobile devices
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 1024;
        if (isMobile) {
          gamesWithTags = gamesWithTags.filter(game => game.mobileoptimized !== 0);
          console.log(`Filtered to ${gamesWithTags.length} mobile-optimized games`);
        }
        
        gamesWithTags.forEach(game => {
          game.tags = [];
          game.platformInfo = platformMap[game.platform];
        });
        
        // Fetch tags in batches of 50 games
        const batchSize = 50;
        for (let i = 0; i < gamesWithTags.length; i += batchSize) {
          const batch = gamesWithTags.slice(i, i + batchSize);
          const gameIds = batch.map(game => game.id).join(',');
          
          try {
            const tagsRes = await apiClient.get(`/games/tags/batch?ids=${gameIds}`);
            const tagsByGame = tagsRes.data;
            
            // Assign tags to each game in the batch
            batch.forEach(game => {
              if (tagsByGame[game.id]) {
                game.tags = tagsByGame[game.id];
              }
            });
          } catch (error) {
            console.error(`Error fetching tags for batch starting with game ${batch[0]?.id}:`, error);
            // Continue with empty tags for this batch
          }
        }

        // Check onboarding status
        const viewedGames = localStorage.getItem('viewedGames');
        const isNewUser = !viewedGames || viewedGames === '[]';
        const onboardingProgress = getOnboardingProgress();
        
        let finalRecommendedGames = [];
        
        // User should see onboarding queue if:
        // 1. They are a new user (no viewedGames), OR
        // 2. They have unfinished onboarding progress
        const shouldShowOnboarding = isNewUser || onboardingProgress;
        
        if (shouldShowOnboarding) {
          console.log('[Onboarding] Loading onboarding queue...');
          if (onboardingProgress) {
            console.log(`[Onboarding] Restoring progress: ${onboardingProgress.currentIndex + 1}/${onboardingProgress.queueGameIds.length} games viewed`);
          } else {
            console.log('[Onboarding] New user detected');
          }
          
          try {
            // Fetch onboarding queue
            const onboardingRes = await apiClient.get(`/onboarding-queue/feed?mobile=${isMobile}`);
            const onboardingGames = onboardingRes.data;
            
            console.log(`[Onboarding] Fetched ${onboardingGames.length} games from onboarding queue`);
            
            // Add platform info and tags to onboarding games
            onboardingGames.forEach(game => {
              game.tags = [];
              game.platformInfo = platformMap[game.platform];
            });
            
            // Fetch tags for onboarding games
            if (onboardingGames.length > 0) {
              const onboardingGameIds = onboardingGames.map(game => game.id).join(',');
              try {
                const tagsRes = await apiClient.get(`/games/tags/batch?ids=${onboardingGameIds}`);
                const tagsByGame = tagsRes.data;
                
                onboardingGames.forEach(game => {
                  if (tagsByGame[game.id]) {
                    game.tags = tagsByGame[game.id];
                  }
                });
              } catch (error) {
                console.error('Error fetching tags for onboarding games:', error);
              }
            }
            
            // Get regular recommendations
            const { games: recommendedGames, debug } = refreshRecommendations(gamesWithTags, platformMap);
            
            // If restoring progress, filter out already-viewed games from the queue
            let remainingOnboardingGames = onboardingGames;
            if (onboardingProgress && onboardingProgress.currentIndex >= 0) {
              // Remove games that were already viewed (up to and including currentIndex)
              remainingOnboardingGames = onboardingGames.slice(onboardingProgress.currentIndex + 1);
              console.log(`[Onboarding] Resuming from game ${onboardingProgress.currentIndex + 2}/${onboardingGames.length}, ${remainingOnboardingGames.length} games remaining`);
            }
            
            // Prepend remaining onboarding games to recommendations
            finalRecommendedGames = [...remainingOnboardingGames, ...recommendedGames];
            setRecommendationDebug(debug);
            
            // Initialize or update onboarding progress
            if (!onboardingProgress) {
              // First time - save the full queue
              saveOnboardingProgress(onboardingGames, -1); // -1 means no games viewed yet
            }
            
            console.log(`[Onboarding] Final queue: ${remainingOnboardingGames.length} onboarding + ${recommendedGames.length} recommended = ${finalRecommendedGames.length} total`);
          } catch (error) {
            console.error('[Onboarding] Error fetching onboarding queue:', error);
            // Fall back to regular recommendations if onboarding fetch fails
            const { games: recommendedGames, debug } = refreshRecommendations(gamesWithTags, platformMap);
            finalRecommendedGames = recommendedGames;
            setRecommendationDebug(debug);
          }
        } else {
          // Existing user who completed onboarding - use regular recommendations
          const { games: recommendedGames, debug } = refreshRecommendations(gamesWithTags, platformMap);
          finalRecommendedGames = recommendedGames;
          setRecommendationDebug(debug);
        }
        
        setGames(finalRecommendedGames);
        setAllGamesData(gamesWithTags); // Now with tags
        
        // Store initial batch debug info (only if we have debug info)
        if (recommendationDebug) {
          const initialBatchInfo = {
            batchNumber: 1,
            timestamp: new Date().toISOString(),
            debug: JSON.parse(JSON.stringify(recommendationDebug)), // Deep copy to prevent reference issues
            gamesCount: finalRecommendedGames.length,
            games: finalRecommendedGames.slice(0, 15).map(game => ({
              id: game.id,
              title: game.titlechn || game.titleeng,
              scores: recommendationDebug.detailedScores?.find(score => score.game.id === game.id) || {},
              weightedScore: recommendationDebug.detailedScores?.find(score => score.game.id === game.id)?.score || 0
            }))
          };
          
          setBatchDebugInfo([initialBatchInfo]);
        }
        
        // Initialize feed with first game centered on PC, or normal layout on mobile
        if (typeof window !== 'undefined' && window.innerWidth > 1024) {
          // On PC: Center the first game by adding empty slots before it
          const emptySlots = Array(2).fill(null); // 2 empty slots before the first game
          const firstGames = finalRecommendedGames.slice(0, 3); // First 3 actual games
          setFeedGames([...emptySlots, ...firstGames]);
        } else {
          // On mobile: Use normal layout
          setFeedGames(finalRecommendedGames.slice(0, MAX_VISIBLE_GAMES));
        }
        
        // Preload metadata for first 8 games to improve user experience
        if (finalRecommendedGames.length > 0) {
          const gamesToPreload = finalRecommendedGames.slice(0, Math.min(8, finalRecommendedGames.length));
          console.log(`[DEBUG] Initial preloading metadata for ${gamesToPreload.length} games`);
          
          gamesToPreload.forEach((game, index) => {
            setTimeout(() => {
              preloadNextGameMetadata(game.id);
            }, index * 120); // Slightly faster initial preloading
            
            // Preload background images for mobile
            if (isMobileView.current && game.cover) {
              setTimeout(() => {
                preloadBackgroundImage(`${API_BASE_URL}${game.cover}`);
              }, index * 60); // Even faster for images
            }
          });
          
          // Also establish initial preload buffer
          setTimeout(() => {
            maintainPreloadBuffer(0);
          }, gamesToPreload.length * 120 + 200);
        }
        
        // Loading will be set to false after image preloading effect completes
      } catch (err) {
        console.error('Error fetching data for feed:', err);
        setError('Failed to load game recommendations. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
    
    // Cleanup function to restore normal scrolling
    return () => {
      if (isMobileView.current) {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.height = '';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // First-time user detection effect
  useEffect(() => {
    const checkFirstTimeUser = () => {
      const hasVisitedFeed = localStorage.getItem('hasVisitedFeed');
      const hasInteractedWithFeed = localStorage.getItem('hasInteractedWithFeed');
      
      if (!hasVisitedFeed && !hasInteractedWithFeed) {
        setIsFirstTimeUser(true);
        setShowInstructionalIcon(true);
        setShowBounceAnimation(true);
        localStorage.setItem('hasVisitedFeed', 'true');
        
        // Auto-hide the instructional elements after 8 seconds if no interaction
        setTimeout(() => {
          if (showInstructionalIcon) {
            setShowInstructionalIcon(false);
            setShowBounceAnimation(false);
          }
        }, 8000);
      }
    };

    // Only check for first-time user on mobile
    if (typeof window !== 'undefined' && window.innerWidth <= 1024 && !loading) {
      checkFirstTimeUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Function to handle first interaction and hide instructional elements
  const handleFirstInteraction = () => {
    if (isFirstTimeUser && showInstructionalIcon) {
      setShowInstructionalIcon(false);
      setShowBounceAnimation(false);
      localStorage.setItem('hasInteractedWithFeed', 'true');
    }
  };

  // Unified metadata loading function with enhanced caching
  const loadGameMetadata = async (gameId) => {
    if (!gameId) return null;
    
    // Check metadata cache first
    if (metadataCache.current.has(gameId)) {
      console.log(`[DEBUG] Using cached metadata for game ${gameId}`);
      return metadataCache.current.get(gameId);
    }
    
    try {
      console.log(`[DEBUG] Loading fresh metadata for game ${gameId}`);
      
      // Load game details and tags in parallel
      const [gameRes, tagsRes] = await Promise.all([
        apiClient.get(`/games/${gameId}`),
        apiClient.get(`/games/${gameId}/tags`).catch(() => ({ data: [] }))
      ]);
      
      const gameData = gameRes.data;
      const gameTags = tagsRes.data;
      
      // Load platform data if available
      let platformData = null;
      let controlsData = null;
      
      if (gameData.platform) {
        try {
          const platformRes = await apiClient.get(`/platforms/${gameData.platform}`);
          platformData = platformRes.data;
          
          // Load controls data from database
          if (platformData.ctrljson) {
            controlsData = platformData.ctrljson;
            
            // Resolve credit IDs to full credit objects
            if (controlsData.creditIds && Array.isArray(controlsData.creditIds) && controlsData.creditIds.length > 0) {
              try {
                // Fetch all credits for these IDs
                const creditPromises = controlsData.creditIds.map(id => 
                  apiClient.get(`/credits/${id}`).catch(err => {
                    console.error(`Error fetching credit ${id}:`, err);
                    return null;
                  })
                );
                
                const creditResponses = await Promise.all(creditPromises);
                const resolvedCredits = creditResponses
                  .filter(res => res !== null)
                  .map(res => res.data);
                
                // Replace empty credits array with resolved credits
                controlsData = {
                  ...controlsData,
                  credits: resolvedCredits
                };
              } catch (err) {
                console.error('Error resolving credit IDs:', err);
              }
            }
          } else {
            controlsData = getDefaultControlsData(platformData.emulator);
          }
        } catch (err) {
          console.error('Error fetching platform data:', err);
        }
      }
      
      // Process manuals
      let manuals = [];
      if (gameData.manual) {
        try {
          const parsedManuals = JSON.parse(gameData.manual);
          if (Array.isArray(parsedManuals) && parsedManuals.length > 0) {
            manuals = parsedManuals;
          } else if (typeof gameData.manual === 'string' && gameData.manual.startsWith('/')) {
            manuals = [{ name: 'Manual', path: gameData.manual }];
          }
        } catch (e) {
          if (typeof gameData.manual === 'string' && gameData.manual.startsWith('/')) {
            manuals = [{ name: 'Manual', path: gameData.manual }];
          }
        }
      }
      
      // Create metadata object
      const metadata = {
        gameData,
        platform: platformData,
        tags: gameTags,
        controls: controlsData,
        manuals,
        gameId
      };
      
      // Cache the metadata
      metadataCache.current.set(gameId, metadata);
      
      // Limit cache size to prevent memory issues - increased limit for better preloading
      if (metadataCache.current.size > 100) {
        const firstKey = metadataCache.current.keys().next().value;
        metadataCache.current.delete(firstKey);
      }
      
      return metadata;
    } catch (err) {
      console.error('Error loading game metadata:', err);
      return null;
    }
  };

  // Enhanced preloading using unified metadata function
  const preloadNextGameMetadata = async (gameId) => {
    if (!gameId) return;
    
    // Always attempt to preload, even if cached, to ensure data freshness
    // But check cache first to avoid unnecessary API calls
    if (metadataCache.current.has(gameId)) {
      console.log(`[DEBUG] Game ${gameId} already cached, skipping preload`);
      return;
    }
    
    try {
      console.log(`[DEBUG] Preloading metadata for game ${gameId}`);
      await loadGameMetadata(gameId);
    } catch (err) {
      console.error('Error preloading next game metadata:', err);
    }
  };

  // Continuous preloading function to maintain a buffer of preloaded games
  const maintainPreloadBuffer = (currentIndex) => {
    if (!games || games.length === 0) return;
    
    // Preload the next 5 games ahead of current position
    const preloadCount = 5;
    const startIndex = currentIndex + 1;
    const endIndex = Math.min(startIndex + preloadCount, games.length);
    
    for (let i = startIndex; i < endIndex; i++) {
      const game = games[i];
      if (game && game.id) {
        // Preload metadata if not cached
        if (!metadataCache.current.has(game.id)) {
          // Stagger preloading to avoid overwhelming the server
          setTimeout(() => {
            preloadNextGameMetadata(game.id);
          }, (i - startIndex) * 100);
        }
        
        // Preload background images for mobile
        if (isMobileView.current && game.cover) {
          setTimeout(() => {
            preloadBackgroundImage(`${API_BASE_URL}${game.cover}`);
          }, (i - startIndex) * 50); // Faster for images
        }
      }
    }
  };

  // Update current game when currentGameIndex changes (simplified - metadata handling moved to handleGameSelect)
  useEffect(() => {
    if (games.length > 0 && currentGameIndex >= 0 && currentGameIndex < games.length) {
      const newGame = games[currentGameIndex];
      console.log(`[DEBUG] currentGameIndex changed to ${currentGameIndex}, game: ${newGame.id}`);
      
      // This effect now only handles cases where currentGameIndex changes without going through handleGameSelect
      // Most game changes should go through handleGameSelect which handles metadata properly
      if (!currentGame || currentGame.id !== newGame.id) {
        console.log(`[DEBUG] Setting current game via useEffect for game ${newGame.id}`);
        setCurrentGame(newGame);
      }
    }
  }, [games, currentGameIndex, currentGame]);

  // Preload images to prevent layout jumps
  useEffect(() => {
    if (games.length > 0 && !imagesPreloaded) {
      const imagesToPreload = feedGames
        .filter(game => game !== null) // Filter out null games
        .map(game => `${API_BASE_URL}${game.cover}`);
      
      let loadedCount = 0;
      const totalImages = imagesToPreload.length;
      
      const preloadImage = (src) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            loadedCount++;
            if (loadedCount === totalImages) {
              setImagesPreloaded(true);
            }
            resolve();
          };
          img.onerror = () => {
            loadedCount++;
            if (loadedCount === totalImages) {
              setImagesPreloaded(true);
            }
            resolve();
          };
          img.src = src;
        });
      };
      
      // Preload all images in parallel
      Promise.all(imagesToPreload.map(src => preloadImage(src)))
        .then(() => {
          // After all images are preloaded, complete the initial load
          setImagesPreloaded(true);
        });
    }
  }, [games, feedGames, imagesPreloaded]);

  // Optimized game details loading with unified metadata function
  useEffect(() => {
    // Reset description scroll on mobile when game changes
    if (isMobileView.current && descriptionRef.current) {
      descriptionRef.current.scrollTop = 0;
    }

    const fetchGameDetails = async () => {
      if (!currentGame) return;
      
      console.log(`[DEBUG] Fetching game details for ${currentGame.id}`);
      setIsLoadingMetadata(true);
      
      // Check if we already have cached metadata
      const cachedMetadata = metadataCache.current.get(currentGame.id);
      
      if (cachedMetadata) {
        console.log(`[DEBUG] Using cached metadata for current game ${currentGame.id}`);
        setPlatform(cachedMetadata.platform);
        setGameTags(cachedMetadata.tags);
        setControlsData(cachedMetadata.controls);
        setManuals(cachedMetadata.manuals);
        setActiveManualIndex(0);
        
        // Set ROM versions
        const versions = getRomVersions(cachedMetadata.gameData);
        setRomVersions(versions);
        if (versions.length > 0) {
          setRomVersion(versions[0].type);
        } else {
          setRomVersion('默认');
        }
        
        setIsLoadingMetadata(false);
      } else {
        // Load metadata using unified function
        try {
          const metadata = await loadGameMetadata(currentGame.id);
          
          if (metadata) {
            setPlatform(metadata.platform);
            setGameTags(metadata.tags);
            setControlsData(metadata.controls);
            setManuals(metadata.manuals);
            setActiveManualIndex(0);
            
            // Set ROM versions
            const versions = getRomVersions(metadata.gameData);
            setRomVersions(versions);
            if (versions.length > 0) {
              setRomVersion(versions[0].type);
            } else {
              setRomVersion('默认');
            }
          } else {
            // Clear data if metadata loading failed
            setPlatform(null);
            setGameTags([]);
            setControlsData(null);
            setManuals([]);
            setRomVersions([]);
            setRomVersion('默认');
          }
        } catch (err) {
          console.error('Error loading game metadata:', err);
          // Clear data on error
          setPlatform(null);
          setGameTags([]);
          setControlsData(null);
          setManuals([]);
          setRomVersions([]);
          setRomVersion('默认');
        } finally {
          setIsLoadingMetadata(false);
        }
      }
      
      // Mark initial load as complete
      if (!initialLoadComplete) {
        setInitialLoadComplete(true);
      }
      
      console.log(`[DEBUG] Game details loaded for ${currentGame.id}`);
    };

    fetchGameDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGame, initialLoadComplete]);

  // Check if description is scrollable to conditionally block swipe
  useEffect(() => {
    if (descriptionRef.current) {
      // Use a timeout to allow the DOM to update with the new content
      const timer = setTimeout(() => {
        const element = descriptionRef.current;
        if (element) {
          const isScrollable = element.scrollHeight > element.clientHeight;
          setIsDescriptionScrollable(isScrollable);
        }
      }, 100); // A small delay to ensure correct measurement
      return () => clearTimeout(timer);
    }
  }, [currentGame]);

  // Close version menu on outside click or when switching games
  useEffect(() => {
    const onDocClick = (e) => {
      if (!playComboRef.current) return;
      if (!playComboRef.current.contains(e.target)) {
        setIsVersionMenuOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  useEffect(() => {
    // Close dropdown when game changes to avoid stale state
    setIsVersionMenuOpen(false);
  }, [currentGame]);
  
  // Complete loading when both images are preloaded and initial load is complete
  useEffect(() => {
    if (imagesPreloaded && initialLoadComplete && loading) {
      // Use a small timeout to ensure smooth transition
      const timer = setTimeout(() => {
        setLoading(false);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [imagesPreloaded, initialLoadComplete, loading]);

  // Handle touch start event for swipe detection
  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY; // Track vertical position
    
    // Handle first interaction for instructional icon
    if (showInstructionalIcon) {
      handleFirstInteraction();
    }
    
    // On mobile, prevent default behavior to avoid scrolling
    if (isMobileView.current) {
      e.preventDefault();
    }
  };

  // Handle touch move event for swipe detection
  const handleTouchMove = (e) => {
    if (!isSwiping) {
      setIsSwiping(true);
    }
    touchEndXRef.current = e.touches[0].clientX;
    touchEndYRef.current = e.touches[0].clientY; // Track vertical position
    
    // On mobile, prevent default behavior to avoid scrolling
    if (isMobileView.current) {
      e.preventDefault();
      
      // Calculate vertical swipe distance for mobile
      // Swipe direction tracking removed - not needed for current implementation
    } else {
      // Desktop uses horizontal swipe
      // Swipe direction tracking removed - not needed for current implementation
    }
  };

  // Handle touch end event for swipe detection
  const handleTouchEnd = (e) => {
    if (!isSwiping) return;
    
    // Reset swipe state
    setIsSwiping(false);
    
    if (isMobileView.current) {
      // Mobile uses vertical swipe with reduced threshold for better responsiveness
      const swipeDistanceY = touchEndYRef.current - touchStartYRef.current;
      const mobileSwipeThreshold = 50; // Reduced threshold for mobile
      
      // Check if vertical swipe distance exceeds threshold
      if (Math.abs(swipeDistanceY) > mobileSwipeThreshold) {
        if (swipeDistanceY > 0 && currentGameIndex > 0) {
          // Swipe down - go to previous game
          handleSwipeAnimation('down');
        } else if (swipeDistanceY < 0 && currentGameIndex < games.length - 1) {
          // Swipe up - go to next game
          handleSwipeAnimation('up');
        }
      }
    } else {
      // Desktop uses horizontal swipe
      const swipeDistance = touchEndXRef.current - touchStartXRef.current;
      
      // Check if horizontal swipe distance exceeds threshold
      if (Math.abs(swipeDistance) > swipeThreshold) {
        if (swipeDistance > 0 && currentGameIndex > 0) {
          // Swipe right - go to previous game
          handleSwipeAnimation('right');
        } else if (swipeDistance < 0 && currentGameIndex < games.length - 1) {
          // Swipe left - go to next game
          handleSwipeAnimation('left');
        }
      }
    }
    
    // Swipe direction tracking removed - not needed for current implementation
  };

  // Handle swipe animation and navigation
  const handleSwipeAnimation = (direction) => {
    if (gameDetailsRef.current) {
      // Apply appropriate animation class based on swipe direction
      if (direction === 'left' || direction === 'up') {
        // Left or Up swipe - next game
        setSwipeAnimation(isMobileView.current ? 'swipe-out-to-up' : 'swipe-out-to-left');
        setTimeout(() => {
          handleScroll();
          setSwipeAnimation(isMobileView.current ? 'swipe-in-from-down' : 'swipe-in-from-right');
        }, 300); // Match animation duration
      } else if (direction === 'right' || direction === 'down') {
        // Right or Down swipe - previous game
        setSwipeAnimation(isMobileView.current ? 'swipe-out-to-down' : 'swipe-out-to-right');
        setTimeout(() => {
          handlePrevGame();
          setSwipeAnimation(isMobileView.current ? 'swipe-in-from-up' : 'swipe-in-from-left');
        }, 300); // Match animation duration
      }
      
      // Reset animation class after completing the transition
      setTimeout(() => {
        setSwipeAnimation('');
      }, 600); // Total animation duration
    }
  };

  // Handle scroll to navigate through games
  const handleScroll = () => {
    if (isTransitioning || currentGameIndex >= games.length - 1) return;

    setIsTransitioning(true);
    
    // Check if a game is currently playing and exit it first
    if (isPlaying) {
      handleExitGame();
    }

    // Calculate the new index
    const nextIndex = currentGameIndex + 1;
    let adjustedIndex = nextIndex;

    let updatedGames = games;

    // If we are near the end (within last 3 games), append a new batch of recommendations
    // We load new games earlier (within last 3) to ensure smooth transitions
    if (games.length - nextIndex <= 3 && allGamesData.length > 0 && Object.keys(platforms).length > 0) {
      // Get new recommendations with debug info
      const { games: newRecommendations, debug } = refreshRecommendations(allGamesData, platforms);
      
      // Make sure we get at least a few new games to prevent immediate reloading
      const newBatchSize = Math.min(15, newRecommendations.length);
      const newBatch = newRecommendations.slice(0, newBatchSize);
      
      // Update the games array with new recommendations
      updatedGames = [...games, ...newBatch]; // duplicates allowed
      setGames(updatedGames);
      
      // Update the debug information to show the latest recommendations
      setRecommendationDebug(debug);
      
      // Store debug info for this batch
      const batchInfo = {
        batchNumber: batchDebugInfo.length + 1,
        timestamp: new Date().toISOString(),
        debug: JSON.parse(JSON.stringify(debug)), // Deep copy to prevent reference issues
        gamesCount: newBatch.length,
        games: newBatch.map(game => ({
          id: game.id,
          title: game.titlechn || game.titleeng,
          scores: debug.detailedScores.find(score => score.game.id === game.id) || {}
        }))
      };
      
      setBatchDebugInfo(prevBatches => [...prevBatches, batchInfo]);
      
      // Preload metadata for the first few games in the new batch
      const gamesToPreload = newBatch.slice(0, Math.min(5, newBatch.length));
      gamesToPreload.forEach((game, index) => {
        setTimeout(() => {
          preloadNextGameMetadata(game.id);
        }, (index + 1) * 150); // Reduced delay for better responsiveness
      });
      
      // Log for debugging
      console.log(`Added ${newBatch.length} new games, total now: ${updatedGames.length}`);
      console.log('Updated debug information for latest recommendations');
    }

    // Trim queue if it grows too long
    // Only trim games BEHIND the current position to avoid skipping games ahead
    if (updatedGames.length > MAX_QUEUE_LENGTH) {
      const excess = updatedGames.length - MAX_QUEUE_LENGTH;
      // Only trim if we have enough games behind us to trim
      if (adjustedIndex >= excess) {
        // Safe to trim from the beginning
        updatedGames = updatedGames.slice(excess);
        adjustedIndex -= excess;
        setGames(updatedGames);
      } else {
        // Not enough games behind us, trim from the end instead
        // This preserves all games ahead that user hasn't seen yet
        updatedGames = updatedGames.slice(0, MAX_QUEUE_LENGTH);
        setGames(updatedGames);
      }
    }
    
    // First update the current game index
    setCurrentGameIndex(adjustedIndex);
    
    // Record the game view for the new current game
    const newCurrentGame = updatedGames[adjustedIndex];
    if (newCurrentGame && newCurrentGame.id) {
      recordGameView(newCurrentGame.id);
    }
    
    // Preload background image for the new current game on mobile
    if (isMobileView.current && newCurrentGame && newCurrentGame.cover) {
      preloadBackgroundImage(`${API_BASE_URL}${newCurrentGame.cover}`);
    }
    
    // Then update the visible games in the carousel with the updated list
    // This order is important to ensure proper centering
    updateVisibleGames(adjustedIndex, updatedGames);

    // Maintain preload buffer after navigation
    setTimeout(() => {
      maintainPreloadBuffer(adjustedIndex);
    }, 100);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  };
  
  // Handle previous game navigation
  const handlePrevGame = () => {
    if (isTransitioning || currentGameIndex <= 0) return;
    
    setIsTransitioning(true);
    
    // Check if a game is currently playing and exit it first
    if (isPlaying) {
      handleExitGame();
    }
    
    // Calculate the new index
    const prevIndex = currentGameIndex - 1;
    
    // Record the game view for the new current game
    const newCurrentGame = games[prevIndex];
    if (newCurrentGame && newCurrentGame.id) {
      recordGameView(newCurrentGame.id);
    }
    
    // Preload background image for the new current game on mobile
    if (isMobileView.current && newCurrentGame && newCurrentGame.cover) {
      preloadBackgroundImage(`${API_BASE_URL}${newCurrentGame.cover}`);
    }
    
    // Update the visible games in the carousel
    updateVisibleGames(prevIndex);
    
    // Set the new current game index
    setCurrentGameIndex(prevIndex);
    
    // Maintain preload buffer after navigation
    setTimeout(() => {
      maintainPreloadBuffer(prevIndex);
    }, 100);
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  };

  useEffect(() => {
    // Check if window is available (for SSR)
    if (typeof window !== 'undefined') {
      const handleResize = () => {
        // Update visible games when window resizes
        updateVisibleGames(currentGameIndex);
        // Update mobile view status
        isMobileView.current = window.innerWidth <= 1024;
      };
      
      window.addEventListener('resize', handleResize);
      
      // Initial check
      isMobileView.current = window.innerWidth <= 1024;
      
      // Cleanup function
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGameIndex]);

  // Handle manual modal close with animation
  const handleManualClose = async () => {
    if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
      // Mobile: use drag animation
      animateManual(manualScope.current, {
        opacity: [1, 0],
      });

      const yStart = typeof manualY.get() === 'number' ? manualY.get() : 0;

      await animateManual('#manual-drawer', {
        y: [yStart, manualHeight || 400],
      });
    } else {
      // Desktop: restore button opacity when closing
      setTimeout(() => {
        const sideActions = document.querySelector('.feed-side-actions');
        if (sideActions) {
          sideActions.style.opacity = '1';
        }
      }, 100);
    }
    setShowManualSidebar(false);
  };

  // Handle controls modal close with animation
  const handleControlsClose = async () => {
    if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
      // Mobile: use drag animation
      animateControls(controlsScope.current, {
        opacity: [1, 0],
      });

      const yStart = typeof controlsY.get() === 'number' ? controlsY.get() : 0;

      await animateControls('#controls-drawer', {
        y: [yStart, controlsHeight || 400],
      });
    } else {
      // Desktop: restore button opacity when closing
      setTimeout(() => {
        const sideActions = document.querySelector('.feed-side-actions');
        if (sideActions) {
          sideActions.style.opacity = '1';
        }
      }, 100);
    }
    setShowControlsSidebar(false);
  };

  // Toggle controls sidebar
  const toggleControlsSidebar = () => {
    // On desktop, fade out buttons when opening sidebar
    if (typeof window !== 'undefined' && window.innerWidth > 1024 && !showControlsSidebar) {
      const sideActions = document.querySelector('.feed-side-actions');
      if (sideActions) {
        sideActions.style.opacity = '0';
        sideActions.style.transition = 'opacity 0.3s ease';
      }
    }
    
    setShowControlsSidebar(!showControlsSidebar);
    // Close manual sidebar if it's open
    if (showManualSidebar) {
      setShowManualSidebar(false);
    }
    
    // On desktop, fade buttons back in when closing sidebar
    if (typeof window !== 'undefined' && window.innerWidth > 1024 && showControlsSidebar) {
      setTimeout(() => {
        const sideActions = document.querySelector('.feed-side-actions');
        if (sideActions) {
          sideActions.style.opacity = '1';
        }
      }, 300); // Match animation duration
    }
  };
  
  // Toggle manual sidebar
  const toggleManualSidebar = () => {
    // On desktop, fade out buttons when opening sidebar
    if (typeof window !== 'undefined' && window.innerWidth > 1024 && !showManualSidebar) {
      const sideActions = document.querySelector('.feed-side-actions');
      if (sideActions) {
        sideActions.style.opacity = '0';
        sideActions.style.transition = 'opacity 0.3s ease';
      }
    }
    
    setShowManualSidebar(!showManualSidebar);
    // Close controls sidebar if it's open
    if (showControlsSidebar) {
      setShowControlsSidebar(false);
    }
    
    // On desktop, fade buttons back in when closing sidebar
    if (typeof window !== 'undefined' && window.innerWidth > 1024 && showManualSidebar) {
      setTimeout(() => {
        const sideActions = document.querySelector('.feed-side-actions');
        if (sideActions) {
          sideActions.style.opacity = '1';
        }
      }, 300); // Match animation duration
    }
  };
  
  // Toggle debug info display
  // eslint-disable-next-line no-unused-vars
  const toggleDebugInfo = () => {
    setShowDebugInfo(!showDebugInfo);
  };

  // Function to update the visible games in the carousel
  const updateVisibleGames = (centerIndex, gamesArray = games) => {
    // Ensure we always have exactly MAX_VISIBLE_GAMES (5) games visible
    // with the current game in the center when possible
    
    // Calculate the ideal position to center the selected game
    const idealCenter = Math.floor(MAX_VISIBLE_GAMES / 2); // For 5 items, this is 2 (0-indexed)
    const isDesktop = typeof window !== 'undefined' && window.innerWidth > 1024;
    
    // First, ensure we have enough games to display
    if (gamesArray.length === 0) {
      setFeedGames([]);
      return;
    }
    
    // Special handling for desktop when we're at the very beginning
    if (isDesktop && centerIndex < idealCenter) {
      // For the first few games on desktop, add empty slots to center them
      const emptySlots = Array(idealCenter - centerIndex).fill(null);
      const actualGames = gamesArray.slice(0, MAX_VISIBLE_GAMES - emptySlots.length);
      setFeedGames([...emptySlots, ...actualGames]);
      setFeedStartIndex(0);
      return;
    }
    
    // If we don't have enough games yet, just show what we have
    if (gamesArray.length <= MAX_VISIBLE_GAMES) {
      if (isDesktop && gamesArray.length <= idealCenter) {
        // On desktop with few games, still try to center the first one
        const emptySlots = Array(idealCenter).fill(null);
        const remainingSlots = MAX_VISIBLE_GAMES - idealCenter;
        const actualGames = gamesArray.slice(0, Math.min(remainingSlots, gamesArray.length));
        setFeedGames([...emptySlots, ...actualGames]);
      } else {
        setFeedGames([...gamesArray]);
      }
      setFeedStartIndex(0);
      return;
    }
    
    // Calculate start index to place the selected game at the center position
    let startIdx = Math.max(0, centerIndex - idealCenter);
    
    // Adjust if we're near the end of the games array
    if (startIdx + MAX_VISIBLE_GAMES > gamesArray.length) {
      startIdx = Math.max(0, gamesArray.length - MAX_VISIBLE_GAMES);
    }
    
    // Strictly enforce showing exactly MAX_VISIBLE_GAMES
    const endIdx = Math.min(startIdx + MAX_VISIBLE_GAMES, gamesArray.length);
    
    // Create a new array with exactly MAX_VISIBLE_GAMES items
    const visibleGames = gamesArray.slice(startIdx, endIdx);
    
    // Update the feed start index for reference
    setFeedStartIndex(startIdx);
    
    // Set the visible games in the carousel
    setFeedGames(visibleGames);
  };

  // Record game play in localStorage
  const recordGamePlay = (gameData, platformData, tags) => {
    try {
      // Get existing played games from localStorage or initialize empty array
      const playedGames = JSON.parse(localStorage.getItem('playedGames') || '[]');
      const playTime = new Date().toISOString();
      
      // Determine game name (prefer Chinese title if available, otherwise English)
      const name = gameData.titlechn || gameData.titleeng || 'Unknown Game';

      // Get year from game data or platform default year if null
      let year = null;
      if (gameData.year && !isNaN(parseInt(gameData.year))) {
        year = parseInt(gameData.year);
      } else if (platformData && platformData.defaultyear) {
        year = parseInt(platformData.defaultyear);
      }
      
      // Extract only tag IDs from the tags array
      const tagIds = tags.map(tag => tag.id);
      
      // Check if this game is already in the played games array
      const existingGameIndex = playedGames.findIndex(game => game.gameId === gameData.id);
      
      if (existingGameIndex !== -1) {
        // Update the existing entry with new play time
        playedGames[existingGameIndex].playTime = playTime;
        // Don't reset time spent - we'll accumulate it
      } else {
        // Add new game to the played games array
        playedGames.push({
          gameId: gameData.id,
          platformId: platformData.id,
          name: name,
          year: year,
          tags: tagIds,
          playTime: playTime,
          timeSpentSeconds: 0 // Initialize time spent
        });
        
        // Keep only last 50 played games
        if (playedGames.length > 50) {
          playedGames.shift();
        }
      }
      
      // Save back to localStorage
      localStorage.setItem('playedGames', JSON.stringify(playedGames));
    } catch (error) {
      console.error('Error recording game play:', error);
    }
  };
  
  // Update time spent in localStorage
  const updateTimeSpent = () => {
    try {
      if (!currentGame) return;
      
      const playedGames = JSON.parse(localStorage.getItem('playedGames') || '[]');
      
      // Find the current game in the played games array
      const currentGameIndex = playedGames.findIndex(game => game.gameId === currentGame.id);
      
      if (currentGameIndex !== -1) {
        // Update the time spent for this game
        playedGames[currentGameIndex].timeSpentSeconds = timeSpentRef.current;
        localStorage.setItem('playedGames', JSON.stringify(playedGames));
      }
    } catch (error) {
      console.error('Error updating time spent:', error);
    }
  };
  
  // Play game directly in Feed page
  const handlePlayGame = (gameId) => {
    const timestamp = Date.now();
    console.log(`[DEBUG] handlePlayGame called for game ${gameId} at ${timestamp}`);
    
    // Find the game in the games array to ensure we have the latest data
    const targetGame = games.find(game => game.id === gameId);
    
    if (!targetGame) {
      console.error('Game not found:', gameId);
      return;
    }
    
    console.log(`[DEBUG] Starting to play game ${gameId}: ${targetGame.titlechn || targetGame.titleeng} at ${timestamp}`);
    
    // If the target game is not the current game, update to it first
    if (currentGame && currentGame.id !== gameId) {
      console.log('Switching to game:', targetGame.titlechn || targetGame.titleeng);
      const correctIndex = games.findIndex(game => game.id === gameId);
      if (correctIndex >= 0) {
        handleGameSelect(correctIndex);
        // Schedule the game to be played after state updates with longer delay to ensure proper state sync
        setTimeout(() => {
          handlePlayGame(gameId);
        }, 300);
        return;
      }
    }
    
    // Get the current ROM versions for the target game
    const gameRomVersions = getRomVersions(targetGame);
    
    // Set the selected ROM data based on the current ROM version
    if (gameRomVersions && gameRomVersions.length > 0) {
      // Find the selected version, defaulting to the first one if not found
      const selectedVersion = gameRomVersions.find(v => v.type === romVersion) || gameRomVersions[0];
      
      // Update ROM state synchronously before setting isPlaying
      setSelectedRomPath(selectedVersion.path);
      setSelectedRomData(selectedVersion);
      
      console.log(`[DEBUG] ROM data set for game ${gameId}:`, selectedVersion.type, 'from path:', selectedVersion.path);
      
      // Set the game to play mode immediately after ROM data is set (remove timeout)
      setIsPlaying(true);
      console.log(`[DEBUG] Game ${gameId} is now playing`);
    } else if (targetGame.rom) {
      // Fallback to direct ROM path if no versions array
      setSelectedRomPath(targetGame.rom);
      setSelectedRomData({ path: targetGame.rom, type: '默认' });
      
      console.log(`[DEBUG] Using direct ROM path for game ${gameId}:`, targetGame.rom);
      
      // Set the game to play mode immediately after ROM data is set (remove timeout)
      setIsPlaying(true);
      console.log(`[DEBUG] Game ${gameId} is now playing with direct ROM`);
    } else {
      console.error('No ROM found for game:', targetGame.id);
      setIsPlaying(false);
      return;
    }
    
    // Record game play in localStorage
    if (currentGame && platform) {
      recordGamePlay(currentGame, platform, gameTags);
      
      // Check if we should increment view count based on playedGames
      const playedGames = JSON.parse(localStorage.getItem('playedGames') || '[]');
      const currentGameEntry = playedGames.find(game => game.gameId === currentGame.id);
      
      // Only increment view if the game wasn't played recently (within the last 30 seconds)
      const wasPlayedRecently = currentGameEntry && 
        currentGameEntry.timeSpentSeconds && 
        currentGameEntry.timeSpentSeconds < 30;
      
      if (!wasPlayedRecently && currentGame.id) {
        try {
          apiClient.post(`/games/${currentGame.id}/view`)
            .then(response => {
              if (response.data.incremented) {
                console.log('View count incremented for game:', currentGame.id);
              } else {
                console.log('View not counted by backend:', response.data.message);
              }
            })
            .catch(viewErr => {
              console.error('Error handling view count:', viewErr);
            });
        } catch (viewErr) {
          console.error('Error handling view count:', viewErr);
        }
      } else {
        console.log('Game played recently (under 30 seconds), not incrementing view count');
      }
      
      // Start timer for tracking time spent
      timeSpentRef.current = 0;
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      
      timerIntervalRef.current = setInterval(() => {
        timeSpentRef.current += 1;
        updateTimeSpent();
      }, 1000);
    }
  };

  // Exit game mode
  const handleExitGame = () => {
    setIsPlaying(false);
    
    // Stop timer when exiting game
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
      console.log('Final time spent:', timeSpentRef.current, 'seconds');
    }
  };

  // Helper function to generate default controls data - matching Game.js implementation
  const getDefaultControlsData = (emulator) => {
    // Default controls data based on emulator type
    switch (emulator) {
      case 'ejs':
        return {
          controls: {
            player1: [
              { key: '方向键', action: '方向' },
              { key: 'X', action: '确认 / A' },
              { key: 'Z', action: '取消 / B' },
              { key: 'A', action: 'L' },
              { key: 'S', action: 'R' },
              { key: 'Q', action: 'SELECT' },
              { key: 'W', action: 'START' }
            ],
            player2: [
              { key: 'I/J/K/L', action: '方向' },
              { key: '1', action: '确认 / A' },
              { key: '2', action: '取消 / B' },
              { key: '5', action: 'L' },
              { key: '6', action: 'R' },
              { key: '8', action: 'SELECT' },
              { key: '9', action: 'START' }
            ]
          },
          notices: [
            { type: 'hint', text: '提示：键位可能因游戏而异，部分游戏可能需要特殊按键。' },
            { type: 'info', text: '游戏加载后，点击游戏画面以激活控制。' }
          ],
          credits: [
            {
              name: 'EmulatorJS',
              website: 'https://emulatorjs.org/',
              github: 'https://github.com/EmulatorJS/EmulatorJS'
            }
          ]
        };
      case 'ruffle':
        return {
          controls: {
            player1: [],
            player2: []
          },
          notices: [
            { type: 'info', text: '点击游戏画面以激活控制。' },
            { type: 'hint', text: '使用鼠标和键盘控制Flash游戏。' }
          ],
          credits: [
            {
              name: 'Ruffle',
              website: 'https://ruffle.rs/',
              github: 'https://github.com/ruffle-rs/ruffle'
            }
          ]
        };
      default:
        return {
          controls: {
            player1: [
              { key: '方向键', action: '方向' },
              { key: 'Z', action: 'A按钮' },
              { key: 'X', action: 'B按钮' },
              { key: 'A', action: 'X按钮' },
              { key: 'S', action: 'Y按钮' },
              { key: 'Enter', action: 'Start' },
              { key: 'Shift', action: 'Select' }
            ],
            player2: []
          },
          notices: [
            { type: 'info', text: '点击游戏画面以激活控制。' },
            { type: 'hint', text: '使用键盘控制游戏，按键映射如上所示。' }
          ],
          credits: []
        };
    }
  };

  // Function to get ROM versions for a game
  const getRomVersions = (game) => {
    if (!game) return [];
    
    try {
      // Check if game.rom is a JSON string
      if (typeof game.rom === 'string' && game.rom.startsWith('[')) {
        const romData = JSON.parse(game.rom);
        if (Array.isArray(romData)) {
          return romData;
        }
      }
    } catch (e) {
      console.error('Error parsing ROM JSON:', e);
    }
    
    // Fallback to legacy format
    const versions = [];
    if (game.rom) versions.push({ type: '默认', path: game.rom });
    if (game.jpnrom) versions.push({ type: '日版', path: game.jpnrom });
    if (game.chnrom) versions.push({ type: '简中版', path: game.chnrom });
    
    return versions;
  };

  // Handle ROM version change
  // eslint-disable-next-line no-unused-vars
  const handleRomVersionChange = (e) => {
    setRomVersion(e.target.value);
  };

  // Handle manual tab change
  const handleManualChange = (index) => {
    setActiveManualIndex(index);
  };

  // Unified loading state - only show loading when we're actually loading
  if (loading || !imagesPreloaded || !initialLoadComplete) {
    return (
      <div className="feed-wrapper">
        <Navbar />
        <div className="feed-container">
          <div className="feed-loading-container">
            <LoadingLogo />
            <div className="loading-text">加载推荐游戏中...</div>
            <FunFact />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feed-wrapper">
        <Navbar />
        <div className="feed-container">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }
  
  // Safety check - if no games or current game, show error
  if (games.length === 0 || !currentGame) {
    return (
      <div className="feed-wrapper">
        <Navbar />
        <div className="feed-container">
          <div className="error-message">无法加载游戏数据，请刷新页面重试。</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`feed-wrapper ${isPlaying ? 'playing-mode' : ''}`}>
      <Navbar />

      <div className="feed-side-actions">
        {manuals.length > 0 && (
          <button 
            className={`feed-manual-button ${platform ? 'with-controls-button' : 'solo-button'} ${isPlaying ? 'playing-mode-manual' : ''}`}
            onClick={toggleManualSidebar}
          >
            查看手册
          </button>
        )}

        <button 
          className={`feed-controls-button ${manuals.length > 0 ? 'with-manual-button' : 'solo-button'} ${isPlaying ? 'playing-mode-controls' : ''}`}
          onClick={toggleControlsSidebar}
        >
          查看控制
        </button>
      </div>

      <div className="feed-container">
        {isPlaying ? (
          <div className="feed-game-content">
            {/* Exit game button */}
            <button className="feed-exit-game-button" onClick={handleExitGame}>
              <span className="back-icon">←</span>返回
            </button>
            
            {/* Emulator Container */}
            {currentGame && platform && memoizedGame && !isLoadingMetadata && memoizedGame.id === currentGame.id ? (
              <>
                <EmulatorContainer
                  key={`emulator-${currentGame.id}-${memoizedGame.rom}`}
                  game={memoizedGame}
                  platform={platform}
                  selectedRomVersion={romVersion}
                  romVersions={romVersions}
                  pageContext="feed"
                />
                
                {/* Mobile Manual and Controls Buttons - show on mobile when game is available */}
                {(typeof window !== 'undefined' && window.innerWidth <= 1024) && (
                  <>
                    {/* Mobile Favorite Button - show above manual and controls */}
                    <div className="mobile-favorite-container">
                      <button 
                        className={`mobile-favorite-button ${isFavorite(currentGame.id) ? 'favorited' : ''}`}
                        onClick={() => toggleFavorite(currentGame.id)}
                        title={isFavorite(currentGame.id) ? '取消收藏' : '添加到收藏'}
                      >
                        <span className="mobile-button-icon">
                          {isFavorite(currentGame.id) ? '★' : '☆'}
                        </span>
                        <span className="mobile-button-text">
                          {isFavorite(currentGame.id) ? '已收藏' : '收藏'}
                        </span>
                      </button>
                    </div>
                    
                    <div className="mobile-game-actions">
                      <button 
                        className="mobile-manual-button"
                        onClick={toggleManualSidebar}
                        disabled={!manuals || manuals.length === 0}
                      >
                        <span className="mobile-button-icon">
                          <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                            <path d="M833.728 340.128l-163.904-178.144c-7.744-8.448-18.4-15.552-29.824-21.28v211.2l202.4 0.064c-2.752-4.192-5.536-8.448-8.64-11.84" fill="currentColor"></path>
                            <path d="M640 576h-256a32 32 0 1 1 0-64h256a32 32 0 1 1 0 64m0 160h-256a32 32 0 1 1 0-64h256a32 32 0 1 1 0 64m223.808-320h-223.744A64.128 64.128 0 0 1 576 351.872V128H223.808A64 64 0 0 0 160 192.064v639.84A64 64 0 0 0 223.744 896h576.512A64 64 0 0 0 864 831.84V417.632c0-0.544-0.16-1.12-0.192-1.664" fill="currentColor"></path>
                          </svg>
                        </span>
                        <span className="mobile-button-text">查看手册</span>
                      </button>
                      
                      <button 
                        className="mobile-controls-button"
                        onClick={toggleControlsSidebar}
                      >
                        <span className="mobile-button-icon">
                          <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                            <path d="M738.538 292 285.478 292C163.082 292 64 382.916 64 510.836 64 638.838 163.082 732 285.478 732l453.058 0C860.98 732 960 638.838 960 510.836 960 382.916 860.98 292 738.538 292zM400 533.394c0 5.396-4.842 10.606-10.476 10.606L320 544l0 70.278c0 5.66-6.208 9.722-11.606 9.722l-42.854 0c-5.256 0-9.542-3.836-9.542-9.066L255.998 544 185.722 544c-5.66 0-9.722-6.208-9.722-11.606l0-42.854c0-5.256 3.836-9.542 9.066-9.542L256 479.998l0-69.644c0-5.612 3.708-10.356 9.106-10.356l44.29 0c5.396 0 10.606 4.842 10.606 10.476L320.002 480l69.644 0c5.612 0 10.356 3.708 10.356 9.106L400.002 533.394zM639.532 550.972c-21.472 0-38.902-17.174-38.902-38.306 0-21.13 17.43-38.336 38.902-38.336 21.566 0 38.996 17.206 38.996 38.336C678.528 533.798 661.098 550.972 639.532 550.972zM724.628 634.602c-21.472 0-38.902-17.058-38.902-38.168 0-21.278 17.43-38.306 38.902-38.306 21.566 0 38.996 17.03 38.996 38.306C763.624 617.542 746.196 634.602 724.628 634.602zM724.628 467.23c-21.472 0-38.902-17.158-38.902-38.292 0-21.134 17.43-38.338 38.902-38.338 21.566 0 38.996 17.204 38.996 38.338C763.624 450.072 746.196 467.23 724.628 467.23zM809.724 550.972c-21.458 0-38.884-17.174-38.884-38.306 0-21.13 17.428-38.336 38.884-38.336 21.474 0 38.904 17.206 38.904 38.336C848.628 533.798 831.2 550.972 809.724 550.972z" fill="currentColor"></path>
                          </svg>
                        </span>
                        <span className="mobile-button-text">查看控制</span>
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="feed-emulator-loading">
                <div className="loading-text">{isLoadingMetadata ? '加载游戏数据中...' : '加载游戏中...'}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="feed-main-content">
            {/* Background blur using current game cover */}
            <div 
              className="feed-background-blur" 
              style={{
                backgroundImage: `url(${API_BASE_URL}${currentGame.cover})`
              }}
            />
          
          {/* Game details overlay */}
          <div 
            className={`feed-game-details ${swipeAnimation} ${showBounceAnimation ? 'game-bounce-animation' : ''}`} 
            ref={gameDetailsRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="feed-game-cover-container">
              <LazyLoadImage
                key={currentGame.id}
                src={`${API_BASE_URL}${currentGame.cover}`}
                alt={currentGame.titleeng || currentGame.titlechn}
                effect="blur"
                className="feed-game-cover"
                wrapperClassName="feed-game-cover-wrapper"
              />
              
              {/* Moved play button under the cover image */}
              <div className="feed-cover-actions">
                {/* Desktop play combo - similar to mobile but for desktop */}
                {!(typeof window !== 'undefined' && window.innerWidth <= 1024) && romVersions.length > 1 ? (
                  <div className="desktop-play-combo" ref={playComboRef}>
                    <button
                      className="desktop-play-main"
                      onClick={() => handlePlayGame(currentGame.id)}
                    >
                      <span className="play-icon">▶</span>
                      <span className="desktop-play-text">立即游玩</span>
                    </button>
                    <button
                      className={`desktop-play-caret ${isVersionMenuOpen ? 'open' : ''}`}
                      aria-label="切换版本"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsVersionMenuOpen((v) => !v);
                      }}
                    >
                      <span className="version-badge">{romVersion || '版本'}</span>
                      <span className="caret-icon">▾</span>
                    </button>
                    {isVersionMenuOpen && (
                      <div className="desktop-version-dropdown">
                        {romVersions.map((rom, index) => (
                          <button
                            key={`desktop-ver-${index}`}
                            className={`version-option ${rom.type === romVersion ? 'active' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setRomVersion(rom.type);
                              setIsVersionMenuOpen(false);
                            }}
                          >
                            {rom.type}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <button 
                    className="feed-play-button" 
                    onClick={() => handlePlayGame(currentGame.id)}
                  >
                    <span className="play-icon">▶</span>
                    立即游玩
                  </button>
                )}
                
                {/* Desktop Favorite button - moved below play button */}
                {!(typeof window !== 'undefined' && window.innerWidth <= 1024) && (
                  <button 
                    className={`feed-favorite-button ${isFavorite(currentGame.id) ? 'favorited' : ''}`}
                    onClick={() => toggleFavorite(currentGame.id)}
                    title={isFavorite(currentGame.id) ? '取消收藏' : '添加到收藏'}
                  >
                    <span className="favorite-icon">
                      {isFavorite(currentGame.id) ? '★' : '☆'}
                    </span>
                    <span className="favorite-text">
                      {isFavorite(currentGame.id) ? '已收藏' : '收藏'}
                    </span>
                  </button>
                )}
              </div>
            </div>
            
            <div className="feed-game-info">
              <h1 className="feed-game-title" lang={currentGame.titleeng ? 'en' : 'zh'}>
                {currentGame.titleeng || currentGame.titlechn}
              </h1>
              {currentGame.titleeng && currentGame.titlechn && (
                <h2 className="feed-game-subtitle">{currentGame.titlechn}</h2>
              )}
              
              <div className="feed-game-metadata">
                <div className="feed-metadata-section">
                  {isLoadingMetadata ? (
                    <div className="feed-metadata-loading">加载中...</div>
                  ) : (
                    <>
                      {/* Platform and Year on same line for mobile only */}
                      {typeof window !== 'undefined' && window.innerWidth <= 768 && (platform || currentGame.year) && (
                        <div className="feed-platform-year-container">
                          {platform && (
                            <div className="feed-metadata-item">
                              <span className="feed-metadata-label">平台:</span> 
                              <span className="feed-metadata-value">{platform.namechn || platform.nameeng || '未知'}</span>
                            </div>
                          )}
                          
                          {currentGame.year && (
                            <div className="feed-metadata-item">
                              <span className="feed-metadata-label">发行年份:</span> 
                              <span className="feed-metadata-value">{currentGame.year}</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Show all metadata on desktop */}
                      {typeof window !== 'undefined' && window.innerWidth > 768 && (
                        <>
                          {platform && (
                            <div className="feed-metadata-item">
                              <span className="feed-metadata-label">平台:</span> 
                              <span className="feed-metadata-value">{platform.namechn || platform.nameeng || '未知'}</span>
                            </div>
                          )}
                          
                          {currentGame.year && (
                            <div className="feed-metadata-item">
                              <span className="feed-metadata-label">发行年份:</span> 
                              <span className="feed-metadata-value">{currentGame.year}</span>
                            </div>
                          )}
                          
                          {currentGame.dev && (
                            <div className="feed-metadata-item">
                              <span className="feed-metadata-label">开发者:</span> 
                              <span className="feed-metadata-value">{currentGame.dev}</span>
                            </div>
                          )}
                          
                          {currentGame.publisher && (
                            <div className="feed-metadata-item">
                              <span className="feed-metadata-label">发行商:</span> 
                              <span className="feed-metadata-value">{currentGame.publisher}</span>
                            </div>
                          )}
                        </>
                      )}

                      {/* Hide translator element */}
                      {/* {currentGame.translator && (
                        <div className="feed-metadata-item">
                          <span className="feed-metadata-label">汉化组:</span> 
                          <span className="feed-metadata-value">{currentGame.translator}</span>
                        </div>
                      )} */}
                    </>
                  )}
                </div>
                
                {isLoadingMetadata && gameTags.length === 0 ? (
                  <div className="feed-tags-loading">加载标签中...</div>
                ) : (
                  gameTags.length > 0 && (
                    <div className="feed-game-tags">
                      <div className="feed-tags-container">
                        {gameTags.map(tag => (
                          <span 
                            key={tag.id} 
                            className="feed-tag"
                            style={{ borderColor: tag.color || '#6c757d' }}
                          >
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
              
              <div 
                className="feed-game-description" 
                ref={descriptionRef}
                onTouchStart={(e) => {
                  // Only stop propagation when description is scrollable
                  // This preserves desktop behavior and lets parent TikTok-like swipe work on mobile when not scrollable
                  if (isDescriptionScrollable) {
                    e.stopPropagation();
                  }
                }}
                onTouchMove={(e) => {
                  // Only stop propagation when description is scrollable
                  // This preserves desktop behavior and lets parent TikTok-like swipe work on mobile when not scrollable
                  if (isDescriptionScrollable) {
                    e.stopPropagation();
                  }
                }}
              >
                {!currentGame.desc || (currentGame.desc.replace(/[^\u4e00-\u9fa5]/g, '').length < 15) 
                  ? "暂无游戏简介" 
                  : currentGame.desc}
              </div>
              

              
              {/* Mobile-specific play button (appears below description) */}
              {(typeof window !== 'undefined' && window.innerWidth <= 1024) && (
                <div className="mobile-play-container" ref={playComboRef}>
                  {romVersions.length > 1 ? (
                    <div className="mobile-play-combo">
                      <button
                        className="mobile-play-main"
                        onClick={() => {
                          if (currentGame && currentGame.id) {
                            handlePlayGame(currentGame.id);
                          }
                        }}
                      >
                        <span className="play-icon">▶</span>
                        <span className="mobile-play-text">立即游玩</span>
                      </button>
                      <button
                        className={`mobile-play-caret ${isVersionMenuOpen ? 'open' : ''}`}
                        aria-label="切换版本"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsVersionMenuOpen((v) => !v);
                        }}
                      >
                        <span className="version-badge">{romVersion || '版本'}</span>
                        <span className="caret-icon">▾</span>
                      </button>
                      {isVersionMenuOpen && (
                        <div className="version-dropdown">
                          {romVersions.map((rom, index) => (
                            <button
                              key={`ver-${index}`}
                              className={`version-option ${rom.type === romVersion ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setRomVersion(rom.type);
                                setIsVersionMenuOpen(false);
                              }}
                            >
                              {rom.type}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <button 
                      className="feed-play-button mobile-play-button" 
                      onClick={() => {
                        if (currentGame && currentGame.id) {
                          handlePlayGame(currentGame.id);
                        }
                      }}
                    >
                      <span className="play-icon">▶</span>
                      立即游玩
                    </button>
                  )}
                  
                  {/* First-time user instructional icon */}
                  {showInstructionalIcon && (
                    <div className={`instructional-icon ${showBounceAnimation ? 'bounce-animation' : ''}`}>
                      <div className="icon-container">
                        <svg viewBox="0 0 24 24" className="chevron-up-icon">
                          <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" fill="currentColor"/>
                        </svg>
                      </div>
                      <span className="instruction-text">向上滑动</span>
                    </div>
                  )}
                  
                  <div className="swipe-indicators"></div>
                </div>
              )}
            </div>
          </div>
        </div>
        )}
        
        {/* Controls sidebar */}
        {showControlsSidebar && (
          <motion.div
            ref={controlsScope}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={handleControlsClose}
            className="feed-controls-sidebar-overlay"
          >
            <motion.div
              id="controls-drawer"
              ref={controlsDrawerRef}
              onClick={(e) => e.stopPropagation()}
              initial={typeof window !== 'undefined' && window.innerWidth <= 1024 ? { y: '100%' } : { x: '100%' }}
              animate={typeof window !== 'undefined' && window.innerWidth <= 1024 ? { y: '0%' } : { x: '0%' }}
              transition={{
                ease: 'easeInOut',
              }}
              className="feed-controls-sidebar open"
              style={typeof window !== 'undefined' && window.innerWidth <= 1024 ? { y: controlsY } : {}}
              drag={typeof window !== 'undefined' && window.innerWidth <= 1024 ? 'y' : false}
              dragControls={controlsDragControls}
              onDragEnd={() => {
                if (controlsY.get() >= 100) {
                  handleControlsClose();
                }
              }}
              dragListener={false}
              dragConstraints={{
                top: 0,
                bottom: 0,
              }}
              dragElastic={{
                top: 0,
                bottom: 0.5,
              }}
            >
              <div className="drag-handle-container">
                <button
                  onPointerDown={(e) => {
                    if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
                      controlsDragControls.start(e);
                    }
                  }}
                  className="close-sidebar-button drag-handle"
                  onClick={handleControlsClose}
                >
                  {typeof window !== 'undefined' && window.innerWidth > 1024 && (
                    <span className="close-icon">×</span>
                  )}
                </button>
              </div>
              
              <div className="controls-sidebar-content">
                {platform && controlsData && (
                  <TabContainer 
                    platform={platform} 
                    controlsData={controlsData}
                    gameId={currentGame ? String(currentGame.id) : '0'}
                    pageContext="feed"
                    additionalHint={currentGame ? currentGame.additional_hint : null}
                  />
                )}
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Manual sidebar */}
        {showManualSidebar && (
          <motion.div
            ref={manualScope}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={handleManualClose}
            className="feed-manual-sidebar-overlay"
          >
            <motion.div
              id="manual-drawer"
              ref={manualDrawerRef}
              onClick={(e) => e.stopPropagation()}
              initial={typeof window !== 'undefined' && window.innerWidth <= 1024 ? { y: '100%' } : { x: '100%' }}
              animate={typeof window !== 'undefined' && window.innerWidth <= 1024 ? { y: '0%' } : { x: '0%' }}
              transition={{
                ease: 'easeInOut',
              }}
              className="feed-manual-sidebar open"
              style={typeof window !== 'undefined' && window.innerWidth <= 1024 ? { y: manualY } : {}}
              drag={typeof window !== 'undefined' && window.innerWidth <= 1024 ? 'y' : false}
              dragControls={manualDragControls}
              onDragEnd={() => {
                if (manualY.get() >= 100) {
                  handleManualClose();
                }
              }}
              dragListener={false}
              dragConstraints={{
                top: 0,
                bottom: 0,
              }}
              dragElastic={{
                top: 0,
                bottom: 0.5,
              }}
            >
              <div className="drag-handle-container">
                <button
                  onPointerDown={(e) => {
                    if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
                      manualDragControls.start(e);
                    }
                  }}
                  className="close-sidebar-button drag-handle"
                  onClick={handleManualClose}
                >
                  {typeof window !== 'undefined' && window.innerWidth > 1024 && (
                    <span className="close-icon">×</span>
                  )}
                </button>
              </div>
              
              <div className="manual-sidebar-content">
                <h3 className="manual-sidebar-title">游戏手册</h3>
                
                {manuals.length > 0 ? (
                  <div className="manual-section">
                    {/* Manual tab selector */}
                    {manuals.length > 1 && (
                      <div className="manual-selector">
                        <div className="feed-manual-tabs">
                          {manuals.map((manual, index) => (
                            <button
                              key={index}
                              className={`feed-manual-tab ${index === activeManualIndex ? 'active' : ''}`}
                              onClick={() => handleManualChange(index)}
                            >
                              {manual.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Manual display */}
                    <div className="manual-container">
                      <iframe 
                        src={`/flipbook.html?pdf=${API_BASE_URL}${manuals[activeManualIndex].path}`}
                        title={manuals[activeManualIndex].name} 
                        className="manual-frame"
                        allowFullScreen={true}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="no-manual">
                    <p className="no-content">暂无游戏手册</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
        
        {/* Navigation buttons moved to bottom carousel */}
        
        {/* Debug button - always visible */}
        {/* TEMPORARILY DISABLED - Debug Panel
        <button 
          className="feed-debug-button" 
          onClick={toggleDebugInfo}
          title="查看推荐调试信息"
        >
          <i className="fas fa-bug"></i>
        </button>
        */}
        
        {/* Debug info sidebar */}
        {/*
        {recommendationDebug && (
          <div className={`feed-debug-sidebar ${showDebugInfo ? 'open' : ''}`}>
            <button className="close-sidebar-button" onClick={toggleDebugInfo}>
              ×
            </button>
            
            <div className="debug-sidebar-content">
              <h3 className="debug-sidebar-title">推荐引擎调试信息</h3>
              
              <div className="debug-section">
                <h4>批次加载信息</h4>
                <div className="debug-batch-info">
                  <div className="debug-batch-summary">
                    <span>总批次数: {batchDebugInfo.length}</span>
                    <span>总游戏数: {games.length}</span>
                  </div>
                  
                  {batchDebugInfo.map((batch, index) => (
                    <div key={index} className="debug-batch-item">
                      <div className="debug-batch-header">
                        <h5>批次 #{batch.batchNumber} ({batch.gamesCount} 个游戏)</h5>
                        <span className="debug-batch-timestamp">{new Date(batch.timestamp).toLocaleString()}</span>
                      </div>
                      <div className="debug-batch-games">
                        {batch.games.map((game, i) => (
                          <div key={i} className="debug-batch-game">
                            <span>{game.title}</span>
                            {game.scores && (
                              <span className="debug-batch-game-score">
                                总分: {game.weightedScore ? game.weightedScore.toFixed(1) : (game.scores.score ? game.scores.score.toFixed(1) : 'N/A')}
                              </span>
                            )}
                          </div>
                        ))}
                        
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="debug-section">
                <h4>推荐权重</h4>
                <div className="debug-weights">
                  <div>年份权重: {(recommendationDebug.weights.year * 100).toFixed(1)}%</div>
                  <div>标签权重: {(recommendationDebug.weights.tags * 100).toFixed(1)}%</div>
                  <div>质量权重: {(recommendationDebug.weights.quality * 100).toFixed(1)}%</div>
                  <div>新鲜度权重: {(recommendationDebug.weights.freshness * 100).toFixed(1)}%</div>
                  <div>随机权重: {(recommendationDebug.weights.random * 100).toFixed(1)}%</div>
                </div>
              </div>
              
              {recommendationDebug.playedGames && recommendationDebug.playedGames.durationCategories && (
                <div className="debug-section">
                  <h4>游玩时长统计</h4>
                  <div className="debug-duration-categories">
                    <div>短暂 (0-30秒): {recommendationDebug.playedGames.durationCategories.brief || 0}次 <span className="note">(不计入偏好)</span></div>
                    <div>简短 (30秒-3分钟): {recommendationDebug.playedGames.durationCategories.short || 0}次</div>
                    <div>中等 (3-10分钟): {recommendationDebug.playedGames.durationCategories.medium || 0}次</div>
                    <div>较长 (10-60分钟): {recommendationDebug.playedGames.durationCategories.long || 0}次</div>
                    <div>深度 (60分钟+): {recommendationDebug.playedGames.durationCategories.extensive || 0}次</div>
                  </div>
                  <div className="debug-play-counts">
                    <div>原始游玩次数: {recommendationDebug.playedGames.totalPlays || 0}</div>
                    <div>计入偏好的游玩次数: {recommendationDebug.consideredPlays || 0}</div>
                    <div>加权游玩次数: {recommendationDebug.playedGames.totalWeightedPlays || 0}</div>
                  </div>
                </div>
              )}
              
              <div className="debug-section">
                <h4>游戏年份偏好分布</h4>
                {recommendationDebug.yearDistribution && recommendationDebug.yearDistribution.fullDistribution && (
                  <div className="year-distribution-chart">
                    <div className="year-chart">
                      <div className="chart-container">
                        <div className="detailed-distribution">
                          {recommendationDebug.yearDistribution.fullDistribution.map((yearData, i) => (
                            <div
                              key={i}
                              className="year-bar"
                              style={{
                                height: `${Math.max(1, parseFloat(yearData.normalizedScore))}%`,
                                backgroundColor: `rgba(228, 203, 134, ${Math.max(0.2, parseFloat(yearData.normalizedScore)/100)})`
                              }}
                              title={`${yearData.year}: ${yearData.normalizedScore}%`}
                            >
                              {yearData.year % 5 === 0 && (
                                <div className="year-label">
                                  {yearData.year}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {recommendationDebug.yearDistribution && recommendationDebug.yearDistribution.rawCounts && (
                  <div className="year-raw-counts">
                    <h5>原始游玩记录:</h5>
                    <div className="raw-counts-container">
                      {recommendationDebug.yearDistribution.rawCounts.map((y, i) => (
                        <span key={i} className="raw-count-item">
                          {y.year}: {y.count}次
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {recommendationDebug.tags && recommendationDebug.tags.length > 0 && (
                <div className="debug-section">
                  <h4>游戏标签权重</h4>
                  <div className="debug-tags">
                    {recommendationDebug.tags.map((t, i) => (
                      <div key={i} className="debug-tag-item">
                        {t.name}: {t.percentage} ({t.count} 次出现)
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {recommendationDebug.playedGames && recommendationDebug.playedGames.playCountsByGame && recommendationDebug.playedGames.playCountsByGame.length > 0 && (
                <div className="debug-section">
                  <h4>游玩记录 (影响新鲜度)</h4>
                  <div>已游玩不同游戏总数: {recommendationDebug.playedGames.totalGamesPlayed}</div>
                  <div className="debug-played-games">
                    <h5>游玩时长最长的游戏:</h5>
                    {recommendationDebug.playedGames.playCountsByGame.map((item, i) => (
                      <div key={i} className="debug-played-game-item">
                        {item.title}: {item.formattedDuration} ({item.durationCategory}, {item.playCount}次)
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="debug-section">
                <h4>推荐游戏得分</h4>
                <div className="debug-games-list">
                  {recommendationDebug.detailedScores && recommendationDebug.detailedScores.map((item, index) => (
                    <div key={index} className="debug-game-item">
                      <div className="debug-game-header">
                        <span className="debug-game-title">{item.game.titlechn || item.game.titleeng}</span>
                        <span className="debug-game-score">总分 {item.score.toFixed(1)}</span>
                      </div>
                      
                      <div className="debug-game-details">
                        <div className="debug-game-year">
                          <span>年份: {item.yearDisplay || '未知'}</span>
                          <span className="debug-score">分数: {item.yearScore.toFixed(1)} → {item.weightedScores.year}</span>
                        </div>
                        
                        <div className="debug-game-tags">
                          <span>标签匹配: {item.matchedTagsInfo || '无匹配标签'}</span>
                          <span className="debug-score">分数: {item.tagScore.toFixed(1)} → {item.weightedScores.tags}</span>
                        </div>
                        
                        <div className="debug-game-quality">
                          <span>质量分数: {item.qualityScore.toFixed(1)} → {item.weightedScores.quality}</span>
                        </div>
                        
                        <div className="debug-game-freshness">
                          <span>新鲜度: {item.freshnessScore.toFixed(1)} → {item.weightedScores.freshness}</span>
                        </div>
                        
                        <div className="debug-game-random">
                          <span>随机因子: {item.randomScore.toFixed(1)} → {item.weightedScores.random}</span>
                        </div>
                        
                        <div className="debug-game-view-count">
                           <span>浏览次数: {item.viewCount || 0}</span>
                         </div>
                         <div className="debug-game-play-info">
                          <span>游玩记录: {item.playInfo || '未玩过'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="debug-section">
                <h4>原始分数分布</h4>
                <div className="debug-table-container">
                  <table className="debug-table">
                    <thead>
                      <tr>
                        <th>游戏</th>
                        <th>年份分</th>
                        <th>标签分</th>
                        <th>质量分</th>
                        <th>新鲜度</th>
                        <th>随机分</th>
                        <th>游玩时长</th>
                        <th>年份</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recommendationDebug.detailedScores && recommendationDebug.detailedScores.map((item, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'even-row' : 'odd-row'}>
                          <td>{item.game.titlechn || item.game.titleeng}</td>
                          <td>{item.yearScore.toFixed(1)}</td>
                          <td>{item.tagScore.toFixed(1)}</td>
                          <td>{item.qualityScore.toFixed(1)}</td>
                          <td>{item.freshnessScore.toFixed(1)}</td>
                          <td>{item.randomScore.toFixed(1)}</td>
                          <td>
                            {item.playDuration ? 
                              `${Math.floor(item.playDuration / 60)}m ${item.playDuration % 60}s` : 
                              '未玩过'}
                          </td>
                          <td>
                            {item.yearDisplay || '未知'}
                            {item.usingPlatformYear && <span className="platform-year"> (平台)</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
        */}
        
        {/* Bottom feed carousel - visible only on desktop */}
        {typeof window !== 'undefined' && window.innerWidth > 1024 && (
          <div 
            className="feed-carousel" 
            ref={feedContainerRef}
          >
          {/* Navigation buttons moved here - always visible */}
          <button 
            className="carousel-nav-button carousel-prev-button" 
            onClick={() => {
              // Use swipe animation on mobile
              if (window.innerWidth <= 1024) {
                handleSwipeAnimation('down');
              } else {
                handlePrevGame();
              }
            }}
            disabled={currentGameIndex <= 0}
          >
            &#10094;
          </button>
          
          <div className="feed-carousel-inner">
            {feedGames.map((game, idx) => {
              // Handle empty slots (null values) for centering
              if (game === null) {
                return (
                  <motion.div 
                    key={`placeholder-${idx}`} 
                    className="feed-carousel-slot"
                    layout
                    transition={{ layout: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } }}
                  >
                    <div className="feed-carousel-item empty-slot">
                      {/* Empty slot for centering */}
                    </div>
                  </motion.div>
                );
              }
              
              // Determine if this game is the current game
              // Only actual games (not null) can be active
              const isDesktop = typeof window !== 'undefined' && window.innerWidth > 1024;
              const isCurrentGame = game && (isDesktop && currentGameIndex < 2 
                ? idx === 2 // Center position for first few games on desktop
                : feedStartIndex + idx === currentGameIndex);
              
              return (
                <motion.div 
                  key={game.id}
                  className="feed-carousel-slot"
                  layout
                  transition={{ layout: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } }}
                >
                  <div 
                    className={`feed-carousel-item ${isCurrentGame ? 'active' : ''}`}
                    data-game-id={game?.id}
                  >
                    {game && (
                      <>
                        <LazyLoadImage
                          key={game.id}
                          src={`${API_BASE_URL}${game.cover}`}
                          alt={game.titleeng || game.titlechn}
                          effect="blur"
                          className="feed-carousel-image"
                          wrapperClassName="feed-carousel-image-wrapper"
                        />
                        <div className="feed-carousel-title">
                          {game.titleeng && <div className="feed-carousel-title-eng">{game.titleeng}</div>}
                          {game.titlechn && <div className="feed-carousel-title-chn">{game.titlechn}</div>}
                        </div>
                      </>
                    )}
                  </div>
                </motion.div>
              );
            })}
            
            {/* Invisible clickable overlays - positioned to match carousel items */}
            <div className="feed-carousel-overlays">
              {feedGames.map((game, idx) => {
                // Create overlay for every position, but only make non-empty ones clickable
                return (
                  <motion.div 
                    layout
                    transition={{ layout: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } }}
                    key={game ? `overlay-${game.id}` : `overlay-empty-${idx}`}
                    className={`feed-carousel-overlay ${game === null ? 'empty-overlay' : ''}`}
                    onClick={game === null ? undefined : () => {
                      // Calculate the actual game index, accounting for empty slots
                      const isDesktop = typeof window !== 'undefined' && window.innerWidth > 1024;
                      let actualGameIndex;
                      
                      if (isDesktop && currentGameIndex < 2) {
                        // When we have empty slots at the beginning, calculate the real game index
                        const emptySlotCount = feedGames.filter((item, index) => index < idx && item === null).length;
                        actualGameIndex = idx - emptySlotCount;
                      } else {
                        // Normal case - use feedStartIndex + idx
                        actualGameIndex = feedStartIndex + idx;
                      }
                      
                      console.log(`Selecting game: ${game.id} at index ${actualGameIndex}`);
                      handleGameSelect(actualGameIndex);
                    }}
                  />
                );
              })}
            </div>
          </div>
          
          {/* Next button moved here - always visible */}
          <button 
            className="carousel-nav-button carousel-next-button" 
            onClick={() => {
              // Use swipe animation on mobile
              if (window.innerWidth <= 1024) {
                handleSwipeAnimation('up');
              } else {
                handleScroll();
              }
            }}
            disabled={currentGameIndex >= games.length - 1}
          >
            &#10095;
          </button>
          </div>
        )}
        
        {/* Mobile fullscreen indicator - subtle swipe hint */}
        <div className="mobile-fullscreen-indicator"></div>
      </div>
    </div>
  );
};

export default Feed;
